import type { AgentConfiguratorState } from '../types/agentConfigurator';

export const DEFAULT_FINE_TUNING_MODEL_PREFIX = 'pipeline-ft';
export const FINE_TUNING_MODEL_NAME_MAX_LEN = 100;

export interface FineTuningAgentPayloadRow {
  agent_id?: string;
  agent_name: string;
  base_model?: string;
  model_name?: string;
  is_trainable?: boolean;
  filters?: Record<string, unknown>;
  [key: string]: unknown;
}

export function slugifyFineTuningModelSegment(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'model'
  );
}

export function buildFineTuningGroupModelName(prefix: string, baseModel: string): string {
  const trimmedPrefix = prefix.trim() || DEFAULT_FINE_TUNING_MODEL_PREFIX;
  const slug = slugifyFineTuningModelSegment(baseModel);
  return `${trimmedPrefix}-${slug}`.slice(0, FINE_TUNING_MODEL_NAME_MAX_LEN);
}

export function buildFineTuningAgentModelName(prefix: string, agentName: string, index: number): string {
  const trimmedPrefix = prefix.trim() || DEFAULT_FINE_TUNING_MODEL_PREFIX;
  const slug = slugifyFineTuningModelSegment(agentName || `agent-${index + 1}`);
  return `${trimmedPrefix}-${slug}`.slice(0, FINE_TUNING_MODEL_NAME_MAX_LEN);
}

/** Align agent rows with POST /training/jobs expectations for grouped vs per-agent FT. */
export function prepareFineTuningAgentsForApi(
  agents: FineTuningAgentPayloadRow[],
  options: { modelNamePrefix: string; groupByBaseModel: boolean }
): FineTuningAgentPayloadRow[] {
  if (!Array.isArray(agents) || agents.length === 0) return [];

  const prefix = options.modelNamePrefix.trim() || DEFAULT_FINE_TUNING_MODEL_PREFIX;

  if (options.groupByBaseModel) {
    const groupModelByBase = new Map<string, string>();
    return agents.map((agent) => {
      const baseModel = String(agent.base_model || '').trim() || 'model';
      if (!groupModelByBase.has(baseModel)) {
        groupModelByBase.set(baseModel, buildFineTuningGroupModelName(prefix, baseModel));
      }
      return {
        ...agent,
        base_model: baseModel,
        model_name: groupModelByBase.get(baseModel)!,
      };
    });
  }

  return agents.map((agent, index) => ({
    ...agent,
    model_name: buildFineTuningAgentModelName(prefix, String(agent.agent_name || ''), index),
  }));
}

export function resolveFineTuningModelNamePrefix(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim().slice(0, FINE_TUNING_MODEL_NAME_MAX_LEN);
  }
  return DEFAULT_FINE_TUNING_MODEL_PREFIX;
}

export function listFineTuningBaseModelGroups(
  agents: FineTuningAgentPayloadRow[],
  modelNamePrefix: string
): Array<{ base_model: string; model_name: string; agent_names: string[] }> {
  const prefix = resolveFineTuningModelNamePrefix(modelNamePrefix);
  const groups = new Map<string, { model_name: string; agent_names: string[] }>();

  for (const agent of agents) {
    const baseModel = String(agent.base_model || '').trim() || 'model';
    const agentName = String(agent.agent_name || 'Agent').trim() || 'Agent';
    const existing = groups.get(baseModel);
    if (existing) {
      existing.agent_names.push(agentName);
      continue;
    }
    groups.set(baseModel, {
      model_name: buildFineTuningGroupModelName(prefix, baseModel),
      agent_names: [agentName],
    });
  }

  return Array.from(groups.entries()).map(([base_model, value]) => ({
    base_model,
    model_name: value.model_name,
    agent_names: value.agent_names,
  }));
}

export function buildFineTuningAgentsFromConfigurator(
  config: AgentConfiguratorState
): FineTuningAgentPayloadRow[] {
  return [
    {
      agent_name: config.coordinator.name || 'Agent Coordinator',
      agent_id: config.coordinator.agent_id || config.coordinator.id,
      base_model: config.coordinator.model,
      is_trainable: config.coordinator.is_trainable !== false,
    },
    ...config.workers.map((worker, index) => ({
      agent_name: worker.name || `Worker ${index + 1}`,
      agent_id: worker.id,
      base_model: worker.model,
      is_trainable: worker.is_trainable !== false,
    })),
  ].filter((agent) => agent.is_trainable);
}

export function preparePipelineFineTuningTaskConfig(
  config: Record<string, unknown>
): Record<string, unknown> {
  const groupByBaseModel = config.group_by_base_model === true;
  const modelNamePrefix = resolveFineTuningModelNamePrefix(config.model_name);
  const agents = Array.isArray(config.agents) ? (config.agents as FineTuningAgentPayloadRow[]) : [];

  return {
    ...config,
    model_name: modelNamePrefix,
    group_by_base_model: groupByBaseModel,
    agents: prepareFineTuningAgentsForApi(agents, { modelNamePrefix, groupByBaseModel }),
  };
}
